import { environment } from '../../../environments/environment';

const identityUrl = `${environment.base_url}/identity`;
const playlistUrl = `${environment.base_url}/playlist`;
const mediaUrl = `${environment.base_url}/media`;
const displayUrl = `${environment.base_url}/display`;
const pduUrl = `${environment.base_url}/pdu`;
const layoutUrl = `${environment.base_url}/layout`;
const schedulingUrl = `${environment.base_url}/scheduling`;
const reportsUrl = `${environment.base_url}/reports`;
const geofenceUrl = `${environment.base_url}/geofence`;
const tenantProfileUrl = `${environment.base_url}/tenantprofile`;
const zoneUrl = `${geofenceUrl}/zones`;
const vehicleUrl = `${geofenceUrl}/vehicles`;

export const API = {
  // =====================================
  // AUTH APIs
  // =====================================
  AUTH: {
    LOGIN: `${identityUrl}/api/Auth/login`,
    REGISTER: `${identityUrl}/api/Auth/register`,
    REFRESH_TOKEN: `${identityUrl}/api/Auth/refresh-token`,
    SEND_OTP: `${identityUrl}/api/Auth/send-otp`,
    VERIFY_OTP: `${identityUrl}/api/Auth/verify-otp`,
    RESEND_VERIFICATION_EMAIL: `${identityUrl}/api/Auth/resend-verification-email`,
    FORGOT_PASSWORD: `${identityUrl}/api/Auth/forgot-password`,
    RESET_PASSWORD: `${identityUrl}/api/Auth/reset-password`,
    CHANGE_PASSWORD: `${identityUrl}/api/Auth/change-password`,
    LOGOUT: `${identityUrl}/api/Auth/logout`,
    VERIFY_EMAIL: `${identityUrl}/api/Auth/verify-email`,
  },

  // =====================================
  // COUNTRY APIs
  // =====================================
  COUNTRIES: {
    LIST: `${identityUrl}/api/countries`,
    STATES: (countryId: number) => `${identityUrl}/api/countries/${countryId}/states`,
    CITIES: (stateId: number) => `${identityUrl}/api/states/${stateId}/cities`,
  },

  // =====================================
  // PROFILE APIs
  // =====================================
  PROFILE: {
    VIEW: `${identityUrl}/api/me/profile`,
    UPDATE: `${identityUrl}/api/me/profile`,
    OTP_PREFERENCE: `${identityUrl}/api/me/otp-preference`,
    PASSWORD: `${identityUrl}/api/me/password`,
  },

  // =====================================
  // TENANT PROFILE APIs
  // =====================================
  TENANT_PROFILE: {
    URL: tenantProfileUrl,
    PROFILE: `${tenantProfileUrl}/api/tenant-profile`,
    SUBDOMAIN: `${tenantProfileUrl}/api/tenant-profile/subdomain`,
  },

  // =====================================
  // IDENTITY USERS APIs
  // =====================================
  IDENTITY_USERS: {
    LIST: `${identityUrl}/api/Users`,
    CREATE: `${identityUrl}/api/Users`,
    VIEW: (id: number) => `${identityUrl}/api/Users/${id}`,
    UPDATE: (id: number) => `${identityUrl}/api/Users/${id}`,
    DELETE: (id: number) => `${identityUrl}/api/Users/${id}`,
    STATUS: (id: number) => `${identityUrl}/api/Users/${id}/status`,
    INVITE: (id: number) => `${identityUrl}/api/Users/${id}/invite`,
    PROFILE_IMAGE: (id: number) => `${identityUrl}/api/Users/${id}/profile-image`,
  },

  // =====================================
  // ROLES APIs
  // =====================================
  ROLES: {
    LIST: `${identityUrl}/api/roles`,
    CREATE: `${identityUrl}/api/roles`,
    UPDATE: (id: number) => `${identityUrl}/api/roles/${id}`,
    DELETE: (id: number) => `${identityUrl}/api/roles/${id}`,
    PERMISSIONS: (id: number) => `${identityUrl}/api/roles/${id}/permissions`,
  },

  // =====================================
  // PERMISSIONS APIs
  // =====================================
  PERMISSIONS: {
    LIST: `${identityUrl}/api/permissions`,
    UPDATE: `${identityUrl}/api/permissions`,
  },

  // =====================================
  // VERSION CONTROL APIs
  // =====================================
  VERSION_CONTROL: {
    LIST: `${identityUrl}/api/version-control`,
  },

  // =====================================
  // DASHBOARD APIs
  // =====================================
  DASHBOARD: {
    PORTAL: `${identityUrl}/api/dashboard`,
    ADVERTISER: `${geofenceUrl}/advertiser-dashboard`,
  },

  // =====================================
  // PLAYLIST APIs
  // =====================================
  PLAYLISTS: {
    LIST: `${playlistUrl}/playlists`,
    CREATE: `${playlistUrl}/playlists`,
    VIEW: (id: number) => `${playlistUrl}/playlists/${id}`,
    EDIT: (id: number) => `${playlistUrl}/playlists/${id}/edit`,
    SAVE: (id: number) => `${playlistUrl}/playlists/${id}/save`,
    PREVIEW: (id: number) => `${playlistUrl}/playlists/${id}/preview`,
    ASSIGNMENTS: (id: number) => `${playlistUrl}/playlists/${id}/assignments`,
    AVAILABLE_CONTENT: (id: number) => `${playlistUrl}/playlists/${id}/available-content`,
    ITEMS: (id: number) => `${playlistUrl}/playlists/${id}/items`,
    SINGLE_ITEM: (id: number) => `${playlistUrl}/playlists/${id}/items/single`,
    SORT_ORDER: (id: number) => `${playlistUrl}/playlists/${id}/items/sort-order`,
    ITEM: (id: number, itemId: number) => `${playlistUrl}/playlists/${id}/items/${itemId}`,
    ITEM_DURATION: (id: number, itemId: number) =>
      `${playlistUrl}/playlists/${id}/items/${itemId}/duration`,
  },

  // =====================================
  // MEDIA APIs
  // =====================================
  MEDIA_CONTENT: {
    URL: mediaUrl,
    LIST: `${mediaUrl}/contents`,
    REVIEW_QUEUE: `${geofenceUrl}/campaign-workflow/media/review-queue`,
    VIEW: (id: number) => `${mediaUrl}/contents/${id}`,
    APPROVE: (id: number) => `${mediaUrl}/contents/${id}/approve`,
    ASSIGNMENTS: (id: number) => `${mediaUrl}/contents/${id}/assignments`,
    BULK_APPROVE: `${mediaUrl}/contents/bulk-approve`,
    ADVERTISER_BULK_APPROVE: `${mediaUrl}/contents/advertiser-approve/bulk`,
    BULK_REVOKE: `${mediaUrl}/contents/bulk-revoke`,
    BULK_DELETE: `${mediaUrl}/contents/bulk-delete`,
    FILES: `${mediaUrl}/files`,
    FILE_ATTRIBUTES: `${mediaUrl}/files/attributes`,
    GROUPS: (id: number) => `${mediaUrl}/contents/${id}/groups`,
    WIDGETS: `${mediaUrl}/contents/widgets`,
    WIDGET: (id: number) => `${mediaUrl}/contents/widgets/${id}`,
    MEDIA_PICKER: `${mediaUrl}/contents/media-picker`,
    WIDGET_PICKER: `${mediaUrl}/contents/widget-picker`,
    SOCIAL_STORE: `${mediaUrl}/contents/social`,
    SOCIAL_ATTRIBUTES: `${mediaUrl}/contents/social/attributes`,
  },

  // =====================================
  // MEDIA GROUP APIs
  // =====================================
  MEDIA_GROUPS: {
    LIST: `${mediaUrl}/content-groups`,
    CREATE: `${mediaUrl}/content-groups`,
    VIEW: (id: number) => `${mediaUrl}/content-groups/${id}`,
    BULK_DELETE: `${mediaUrl}/content-groups/bulk-delete`,
  },

  // =====================================
  // DISPLAY / DEVICE APIs
  // =====================================
  DISPLAY: {
    URL: displayUrl,

    DEVICES: `${displayUrl}/devices`,
    ADD_PREVIEW: `${displayUrl}/player/add/preview`,
    ADD_CONFIRM: `${displayUrl}/player/add/confirm`,
    DEVICE_FILTERS: `${displayUrl}/devices/filters`,
    CLAIM_DEVICE: `${displayUrl}/devices/claim`,

    DEVICE: (id: number) => `${displayUrl}/devices/${id}`,

    DEFAULT_MEDIA: (id: number) => `${displayUrl}/devices/${id}/default-media`,

    DEVICE_STATUS: (id: number) => `${displayUrl}/devices/${id}/status`,

    DEVICE_ACTION: (id: number, action: string) => `${displayUrl}/devices/${id}/${action}`,

    DEVICE_HEALTH: (id: number) => `${displayUrl}/devices/${id}/health`,

    FIRMWARE_UPDATE: (id: number) => `${displayUrl}/devices/${id}/firmware/update`,

    SCREEN_STATUS: (id: number) => `${displayUrl}/devices/${id}/display-screen-status`,

    SCREEN_CAPTURE: (id: number) => `${displayUrl}/devices/${id}/screen-request`,

    SCREEN_PREVIEW: (id: number) => `${displayUrl}/devices/${id}/preview-request`,

    STORAGE_CLEAR: (id: number) => `${displayUrl}/devices/${id}/storage/clear`,

    MEDIA_DOWNLOAD_STATUS: (id: number) => `${displayUrl}/devices/${id}/media-download-status`,

    BANDWIDTH: (id: number) => `${displayUrl}/devices/${id}/bandwidth`,

    BRIGHTNESS_SCHEDULE: (id: number) => `${displayUrl}/devices/${id}/brightness-schedule`,

    LOCATION: (id: number) => `${displayUrl}/devices/${id}/location`,

    DOWNLOAD_CONFIG: (id: number) => `${displayUrl}/devices/${id}/download-config`,

    EXPORT: `${displayUrl}/devices/export`,
    SEARCH: `${displayUrl}/devices/search`,

    GROUPS: `${displayUrl}/device-groups`,
    GROUP_TREE: `${displayUrl}/device-groups/tree`,
    GROUP_SEARCH: `${displayUrl}/device-groups/search`,

    GROUP: (id: number) => `${displayUrl}/device-groups/${id}`,

    GROUP_ASSIGN_DEVICES: (id: number) => `${displayUrl}/device-groups/${id}/assign-devices`,

    DEVICE_REGISTER: `${displayUrl}/devices/register`,
    DEVICE_REFRESH_TOKEN: `${displayUrl}/devices/refresh-token`,
    DEVICE_HEARTBEAT: `${displayUrl}/devices/heartbeat`,
    DEVICE_TELEMETRY: `${displayUrl}/devices/telemetry`,
    DEVICE_CONFIG: `${displayUrl}/devices/config`,
    DEVICE_SCHEDULE: `${displayUrl}/devices/schedule`,
    DEVICE_FIRMWARE_STATUS: `${displayUrl}/devices/firmware/status`,
    DEVICE_STORAGE_ACK: `${displayUrl}/devices/storage/ack`,
    DEVICE_MEDIA_DELETED: `${displayUrl}/devices/media-deleted`,
    DEVICE_MEDIA_DELETED_ACK: `${displayUrl}/devices/media-deleted`,
    DEVICE_ERROR_LOG: `${displayUrl}/devices/error-log`,
    DEVICE_CONTENT_STATUS: `${displayUrl}/devices/content-status`,
    PLAYER_MODELS: `${displayUrl}/devices/player-models`,
  },

  // =====================================
  // PDU APIs
  // =====================================
  PDU: {
    URL: pduUrl,

    DEVICE_POWER: (id: number) => `${pduUrl}/devices/${id}/pdu-onoff`,

    ROUTER_REBOOT: (id: number) => `${pduUrl}/devices/${id}/router-reboot`,

    DEVICE_SETTING: (id: number) => `${pduUrl}/devices/${id}/pdu-setting`,

    DEVICE_SCHEDULER: (id: number) => `${pduUrl}/devices/${id}/pdu-scheduler`,

    LOGS: `${pduUrl}/reports/pdu-log`,

    LIST: `${pduUrl}/pdus`,
    REGISTER: `${pduUrl}/pdus/register`,
    DEREGISTER: `${pduUrl}/pdus/deregister`,

    ASSIGN_DISPLAY: (id: number) => `${pduUrl}/pdus/${id}/assign-display`,

    POWER: (id: number) => `${pduUrl}/pdus/${id}/onoff`,

    POWER_READINGS: `${pduUrl}/power-readings`,

    POWER_READING_LOGS: (id: string) => `${pduUrl}/power-readings/${id}/logs`,
  },

  // =====================================
  // LAYOUT APIs
  // =====================================
  LAYOUT: {
    URL: layoutUrl,

    LAYOUTS: `${layoutUrl}/layouts`,
    CONTENT_PICKER: `${layoutUrl}/layouts/content-picker`,
    BULK_DELETE_LAYOUTS: `${layoutUrl}/layouts/bulk-delete`,

    LAYOUT: (id: number) => `${layoutUrl}/layouts/${id}`,

    LAYOUT_STATUS: (id: number) => `${layoutUrl}/layouts/${id}/status`,

    LAYOUT_DESIGN: (id: number) => `${layoutUrl}/layouts/${id}/design`,

    LAYOUT_PREVIEW: (id: number) => `${layoutUrl}/layouts/${id}/preview`,

    REGION_CONTENT: (id: number, regionId: number) =>
      `${layoutUrl}/layouts/${id}/regions/${regionId}/content`,

    TEMPLATES: `${layoutUrl}/layout-templates`,
    TEMPLATE_SEARCH: `${layoutUrl}/layout-templates/search`,
    ASPECT_RATIOS: `${layoutUrl}/layout-templates/aspect-ratios`,
    TEMPLATE_EXPORT: `${layoutUrl}/layout-templates/export`,
    BULK_DELETE_TEMPLATES: `${layoutUrl}/layout-templates/bulk-delete`,

    TEMPLATE: (id: number) => `${layoutUrl}/layout-templates/${id}`,

    TEMPLATE_STATUS: (id: number) => `${layoutUrl}/layout-templates/${id}/status`,

    TEMPLATE_DESIGN: (id: number) => `${layoutUrl}/layout-templates/${id}/design`,
  },

  // =====================================
  // SCHEDULING APIs
  // =====================================
  SCHEDULING: {
    URL: schedulingUrl,

    SCHEDULES: `${schedulingUrl}/schedules`,

    SCHEDULE: (id: number) => `${schedulingUrl}/schedules/${id}`,

    ACTIVATE: (id: number) => `${schedulingUrl}/schedules/${id}/activate`,

    DEACTIVATE: (id: number) => `${schedulingUrl}/schedules/${id}/deactivate`,

    ASSIGNMENTS: (id: number) => `${schedulingUrl}/schedules/${id}/assignments`,

    ASSIGNMENT: (id: number, assignmentId: number) =>
      `${schedulingUrl}/schedules/${id}/assignments/${assignmentId}`,

    NOTIFY_DEVICES: (id: number) => `${schedulingUrl}/schedules/${id}/notify-devices`,
  },

  // =====================================
  // REPORTS APIs
  // =====================================
  REPORTS: {
    URL: reportsUrl,

    CATALOGUE: `${reportsUrl}/reports`,
    CAMPAIGN_TELEMETRY_REPORT: `${geofenceUrl}/campaign-telemetry/campaign-report`,

    OFFLINE_PLAYERS: `${reportsUrl}/reports/offline-players`,
    ONLINE_PLAYERS: `${reportsUrl}/reports/online-players`,
    PLAYBACK_SUMMARY: `${reportsUrl}/reports/playback-summary`,
    MEDIA_PLAYBACK: `${reportsUrl}/reports/media-playback`,
    PLAYER_LOG: `${reportsUrl}/reports/player-log`,
    PROOF_OF_PLAY: `${reportsUrl}/reports/proof-of-play`,
    PDU_LOG: `${reportsUrl}/reports/pdu-log`,
    HEARTBEAT: `${reportsUrl}/reports/heartbeat`,
    HEARTBEAT_DOWNTIME: `${reportsUrl}/reports/heartbeat-downtime`,
    IGNITION_PLAYBACK: `${reportsUrl}/reports/ignition-playback`,

    AUDIT_LOGS: `${reportsUrl}/audit-logs`,

    OFFLINE_PLAYERS_EXPORT: `${reportsUrl}/reports/offline-players/export`,

    ONLINE_PLAYERS_EXPORT: `${reportsUrl}/reports/online-players/export`,

    PLAYBACK_SUMMARY_EXPORT: `${reportsUrl}/reports/playback-summary/export`,

    PROOF_OF_PLAY_EXPORT: `${reportsUrl}/reports/proof-of-play/export`,

    MEDIA_PLAYBACK_EXPORT: `${reportsUrl}/reports/media-playback/export`,

    PDU_LOG_EXPORT: `${reportsUrl}/reports/pdu-log/export`,

    HEARTBEAT_EXPORT: `${reportsUrl}/reports/heartbeat/export`,

    HEARTBEAT_DOWNTIME_EXPORT: `${reportsUrl}/reports/heartbeat-downtime/export`,

    IGNITION_PLAYBACK_EXPORT: `${reportsUrl}/reports/ignition-playback/export`,

    AUDIT_LOGS_EXPORT: `${reportsUrl}/audit-logs/export`,
  },

  // =====================================
  // GEOFENCE APIs
  // =====================================
  SLOTS: {
    LIST: `${geofenceUrl}/slots`,
    CREATE: `${geofenceUrl}/slots`,
    BY_ID: (id: number) => `${geofenceUrl}/slots/${id}`,
    UPDATE: (id: number) => `${geofenceUrl}/slots/${id}`,
    DELETE: (id: number) => `${geofenceUrl}/slots/deleteSlot/${id}`,
    GENERATE: `${geofenceUrl}/slots/generate`,
    AVAILABILITY: (id: number) => `${geofenceUrl}/slots/${id}/availability`,
  },

  CAMPAIGNS: {
    UPDATE: (id: number) => `${geofenceUrl}/campaigns/update/${id}`,
    LIST: `${geofenceUrl}/campaigns`,
    CREATE: `${geofenceUrl}/campaigns`,
    BY_ID: (id: number) => `${geofenceUrl}/campaigns/${id}`,
    ZONES: (id: number) => `${geofenceUrl}/campaigns/${id}/zones`,
    SLOTS: (id: number) => `${geofenceUrl}/campaigns/${id}/slots`,
    BUDGET: (id: number) => `${geofenceUrl}/campaigns/${id}/budget`,
    MEDIA: (id: number) => `${geofenceUrl}/campaigns/${id}/media`,
    MEDIA_LIST: `${geofenceUrl}/campaign-workflow/media`,
    MEDIA_UPLOAD: `${geofenceUrl}/campaign-workflow/media/upload`,
    CONFIRM_PAYMENT: (id: number) => `${geofenceUrl}/campaigns/${id}/confirm-payment`,
    QUOTE: (id: number) => `${geofenceUrl}/campaigns/${id}/quote`,
    CREDITS: `${geofenceUrl}/campaigns/credits`,
    VEHICLE_TRACKING: (id: number) => `${geofenceUrl}/campaign-telemetry/campaigns/${id}/vehicle-tracking`,
  },

  GEOFENCE: {
    PLAYER_GEOFENCES: (playerId: number) => `${geofenceUrl}/devices/${playerId}/geofences`,

    DELETE: (geofenceId: number) => `${geofenceUrl}/geofences/${geofenceId}`,

    MAP_DATA: (playerId: number) => `${geofenceUrl}/devices/${playerId}/geofences/map-data`,

    ROUTE_DATA: (playerId: number) => `${geofenceUrl}/devices/${playerId}/geofences/route-data`,
  },

  // =====================================
  // ZONE APIs
  // =====================================
  ZONES: {
    URL: zoneUrl,
    LIST: `${zoneUrl}/List`,
    CREATE: `${zoneUrl}/CreateZone`,
    BY_ID: (id: number) => `${zoneUrl}/getbyIdZone/${id}`,
    UPDATE: (id: number) => `${zoneUrl}/updateZone/${id}`,
    DELETE: (id: number) => `${zoneUrl}/deleteZone/${id}`,
    STATUS: (id: number) => `${zoneUrl}/${id}/status`,
    LIST_TYPES: `${zoneUrl}/ListTypes`,
    CREATE_TYPE: `${zoneUrl}/CreateType`,
    UPDATE_TYPE: (id: number) => `${zoneUrl}/UpdateType/${id}`,
    DELETE_TYPE: (id: number) => `${zoneUrl}/DeleteType/${id}`,
  },

  // =====================================
  // VEHICLE APIs
  // =====================================
  VEHICLES: {
    URL: vehicleUrl,
    LIST: vehicleUrl,
    EXPORT: `${vehicleUrl}/export`,
    CREATE: `${vehicleUrl}/CreateVehicle`,
    BY_ID: (id: number) => `${vehicleUrl}/getVehicle/${id}`,
    UPDATE: (id: number) => `${vehicleUrl}/updateVehicle/${id}`,
    DELETE: (id: number) => `${vehicleUrl}/deleteVehicle/${id}`,
    STATUS: (id: number) => `${vehicleUrl}/${id}/status`,
    ASSIGN_DISPLAY: (id: number) => `${vehicleUrl}/${id}/displays`,
    UNASSIGN_DISPLAY: (id: number, playerId: number) => `${vehicleUrl}/${id}/displays/${playerId}`,
    AVAILABLE_DISPLAYS: (id: number) => `${vehicleUrl}/${id}/available-displays`,
  },
};
